import React, { useEffect, useState } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  emptyString,
  imageSizeAlert,
  fontFamilyStyles,
  surveyTypesConstants,
  themeSettingsConstants,
  themeSettingsLabels,
  themeLogoLimits,
  themeFontSelectStyles,
  modalSize,
  BUTTON_TEXT,
  ACCEPTED_FORMATS,
  ERROR_LOADING_IMAGE,
  toastTypes,
  customerSurveyConstants,
} from "../../constants";
import {
  useSpinner,
  Spinner,
  ColorPicker,
  setProperty,
  customToast,
  handleAsyncAction,
} from "../../../../shared";
import { getConfirmationModal } from "../../../../shared/components/popups/allModals";
import { themeSettingsActions, brandProfileActions } from "../../redux/actions";
import { createThemeFormData, isThemeLogoLargeEnough } from "./utility";
import ThemePreview from "./themePreview";

const { UPDATE_THEME } = themeSettingsConstants;
const { IMAGE_SIZE_ERROR, IMAGE_FORMAT_ERROR } = imageSizeAlert;
const { WIZARD } = surveyTypesConstants;

const ThemeSettings = (props) => {
  // Destructuring props
  const {
    resetTheme,
    existingTheme,
    getThemeSettings,
    getBrandByBrandId,
    updateThemeSettings,
    deleteThemeSettingLogo,
    deletedBrandId,
  } = props;

  const { brandId } = useParams();
  const { handleSubmit } = useForm();

  // Initialize states
  const [formList, setFormList] = useState({
    surveyStyle: WIZARD,
    logoFile: emptyString,
    fontColor: emptyString,
    fontStyle: emptyString,
    mainThemeColor: emptyString,
    progressBarColor: emptyString,
    starColor: emptyString,
  });
  const [showModal, setShowModal] = useState(false);
  const [modalElement, setModalElement] = useState(null);

  // Show loading while a request is running.
  const spinner = useSpinner();

  const onSubmit = () => {
    const {
      logoFile,
      starColor,
      fontColor,
      fontStyle,
      surveyStyle,
      mainThemeColor,
      progressBarColor,
    } = formList;

    updateThemeSettings(
      createThemeFormData(
        {
          logoFile,
          starColor,
          fontColor,
          fontStyle,
          surveyStyle,
          mainThemeColor,
          progressBarColor,
        },
        existingTheme
      ),
      existingTheme.brandID
    );
  };

  const handleThemeFieldChange = (field, value) => {
    setFormList((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    if (!ACCEPTED_FORMATS.includes(file.type)) {
      customToast(IMAGE_FORMAT_ERROR, toastTypes.ERROR);
      e.target.value = "";
      return;
    }

    const img = new Image();

    img.onload = () => {
      if (!isThemeLogoLargeEnough(img.width, img.height)) {
        customToast(IMAGE_SIZE_ERROR, toastTypes.ERROR);
        e.target.value = emptyString;
      } else {
        handleThemeFieldChange("logoFile", file);
      }
    };

    img.onerror = () => {
      // Error loading image
      customToast(ERROR_LOADING_IMAGE, toastTypes.ERROR);
      e.target.value = ""; // Reset file input
    };

    img.src = URL.createObjectURL(file);
  };

  //Fetching the default or updated theme of a survey
  useEffect(() => {
    resetTheme();
    getBrandByBrandId(brandId);
    getThemeSettings(brandId);
    // eslint-disable-next-line
  }, []);

  //Fetching the css variables and changing there values
  useEffect(() => {
    setProperty(customerSurveyConstants.CSS_FONT_COL, formList.fontColor);
    setProperty(customerSurveyConstants.CSS_FONT_FAMILY, formList.fontStyle);
    setProperty(customerSurveyConstants.CSS_BAR_COL, formList.progressBarColor);
    setProperty(customerSurveyConstants.CSS_MAIN_THEME, formList.mainThemeColor);
    setProperty(customerSurveyConstants.CSS_STAR_THEME, formList.starColor);
    // eslint-disable-next-line
  }, [formList]);

  //Use effect to set theme settings
  useEffect(() => {
    if (existingTheme) {
      setFormList({
        ...formList,
        fontColor: existingTheme.fontColor,
        fontStyle: existingTheme.fontStyle,
        surveyStyle: existingTheme.surveyStyle,
        starColor: existingTheme.starColor,
        mainThemeColor: existingTheme.mainThemeColor,
        progressBarColor: existingTheme.progressBarColor,
      });
    }
    //eslint-disable-next-line
  }, [existingTheme]);

  //To open the modal
  const openModal = () => {
    setShowModal(true);
  };

  //To close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  //Setting modal component
  const setComponent = (component) => {
    setModalElement(component);
    openModal();
  };

  //Delete themeSetting logo modal
  const deleteImage = (e) => {
    e.stopPropagation();
    setComponent(
      getConfirmationModal({
        onClose: closeModal,
        Title: BUTTON_TEXT.CONFIRM,
        message: themeSettingsLabels.DELETE_LOGO_MESSAGE,
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        onConfirm: () => {
          handleAsyncAction(deleteThemeSettingLogo(brandId), {
            onSuccess: closeModal,
          });
          handleThemeFieldChange("logoFile", emptyString);
        },
      })
    );
  };

  //If themeSetting logo deleted then get brand data again
  useEffect(() => {
    if (deletedBrandId) {
      getBrandByBrandId(brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedBrandId]);

  return (
    <>
      {spinner(UPDATE_THEME) ? (
        <Spinner />
      ) : (
        <div className="container-fluid py-3">
          {showModal && modalElement}
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="form-theme-settings"
          >
            <div className="notificationForm card shadow border-0 mb-3">
              <div className="card-body p-0">
                <h5 className="px-4 py-2 mb-0">{themeSettingsLabels.THEMES}</h5>
                <hr className="my-0" />
                <div className="row pt-2">
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group input-image-pl">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <input
                            data-testid="customFile"
                            type="file"
                            accept={themeLogoLimits.ACCEPTED_INPUT}
                            className="form-control w83"
                            id="customFile"
                            onChange={(e) => handleFileChange(e)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div
                      className={`form__form-group ${existingTheme?.logo ? emptyString : "d-none"
                        }`}
                    >
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap d-flex align-item-start justify-content-center">
                          <img
                            alt="brand-logo"
                            src={existingTheme?.logo}
                            width={themeLogoLimits.PREVIEW_WIDTH_PX}
                            className="img-fluid rounded"
                          />
                          <FontAwesomeIcon
                            icon={faTimes}
                            size="2xl"
                            className="remove-button ps-2"
                            onClick={(e) => deleteImage(e)}
                            data-testid="delete-brand-logo"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <ColorPicker
                            label={themeSettingsLabels.MAIN_THEME_COLOR}
                            value={formList.mainThemeColor}
                            setvalue={(value) =>
                              handleThemeFieldChange("mainThemeColor", value)
                            }
                            maxLength={themeLogoLimits.COLOR_MAX_LENGTH}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <ColorPicker
                            label={themeSettingsLabels.PROGRESS_BAR_COLOR}
                            value={formList.progressBarColor}
                            setvalue={(value) =>
                              handleThemeFieldChange("progressBarColor", value)
                            }
                            maxLength={themeLogoLimits.COLOR_MAX_LENGTH}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <ColorPicker
                            label={themeSettingsLabels.FONT_COLOR}
                            value={formList.fontColor}
                            setvalue={(value) =>
                              handleThemeFieldChange("fontColor", value)
                            }
                            maxLength={themeLogoLimits.COLOR_MAX_LENGTH}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <div className="col-12 col-7 row color-picker-container">
                            <div className="col-12 col-md-4 color-bar-container">
                              <label
                                htmlFor="fontStyle"
                                className="color-label form-label w100"
                              >
                                {themeSettingsLabels.FONT_STYLE}
                              </label>
                            </div>
                            <div className="col-12 col-md-8 m-0 p-0">
                              <Select
                                name="fontStyle"
                                value={[
                                  {
                                    value:
                                      formList.fontStyle ||
                                      fontFamilyStyles.at(0).value,
                                    name:
                                      formList.fontStyle ||
                                      fontFamilyStyles.at(0).name,
                                  },
                                ]}
                                id="fontStyle"
                                options={[...fontFamilyStyles]}
                                getOptionValue={(option) => option.value}
                                getOptionLabel={(option) => option.name}
                                onChange={(e) =>
                                  handleThemeFieldChange("fontStyle", e.value)
                                }
                                components={{
                                  IndicatorSeparator: () => null,
                                }}
                                isSearchable={true}
                                styles={themeFontSelectStyles}
                                className="text-secondary w-97"
                                classNamePrefix="font-style-select"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="form__form-group">
                      <div className="form__form-group-field">
                        <div className="form__form-group-input-wrap">
                          <ColorPicker
                            label={themeSettingsLabels.STAR_COLOR}
                            value={formList.starColor}
                            setvalue={(value) =>
                              handleThemeFieldChange("starColor", value)
                            }
                            maxLength={themeLogoLimits.COLOR_MAX_LENGTH}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end p-3">
                  <Button
                    type="submit"
                    className="add-button user-permission-button themes-btn px-4"
                  >
                    {BUTTON_TEXT.SAVE}
                  </Button>
                </div>
              </div>
            </div>
          </form>
          <ThemePreview
            surveyStyle={formList.surveyStyle}
            profileData={props.profileData}
            onSurveyStyleChange={(e) =>
              handleThemeFieldChange("surveyStyle", parseInt(e.target.value))
            }
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  profileData: state.individualStoreProfileReducer.profileData,
  existingTheme: state.themeSettingsReducer.existingTheme,
  deletedBrandId: state.themeSettingsReducer.deletedBrandId,
});

const mapDispatchToProps = (dispatch) => ({
  getBrandByBrandId: (brandId) =>
    dispatch(brandProfileActions.getBrandByBrandId(brandId)),
  updateThemeSettings: (data, brandId) =>
    dispatch(themeSettingsActions.updateThemeSettings(data, brandId)),
  getThemeSettings: (id) => dispatch(themeSettingsActions.getThemeSettings(id)),
  resetTheme: () => dispatch(themeSettingsActions.resetTheme()),
  deleteThemeSettingLogo: (brandId) =>
    dispatch(themeSettingsActions.deleteThemeSettingLogo(brandId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeSettings);
